import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

const normalizeUser = (userData) => {
  if (!userData) return null;

  return {
    ...userData,
    addressBook: Array.isArray(userData.addressBook) ? userData.addressBook : [],
    companySharingEnabled: userData.companySharingEnabled !== false,
    companyName: userData.companyName || '',
    companyKey: userData.companyKey || null,
    preferredLanguage: userData.preferredLanguage || null,
    country: userData.country || '',
  };
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pingTimerRef = React.useRef(null);

  const logout = async (forced = false) => {
    // stop heartbeat
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    try {
      const token = localStorage.getItem('token');

      // Se forced=true (sessione scaduta / token invalido) NON chiamare logout backend
      if (token && !forced) {
        try {
          await authService.logout(token);
        } catch (e) {
          // non bloccare il logout client-side se il backend non risponde
          console.error('Logout backend failed:', e);
        }
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Svuota anche il carrello locale per evitare riuso tra account
      localStorage.removeItem('cart');
      setUser(null);

      if (forced) {
        alert('Sessione scaduta, effettuare nuovamente il login.');
      }

      window.location.href = '/login';
    } catch (err) {
      console.error('Errore durante il logout:', err);
      // fallback duro
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('cart');
      setUser(null);
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Qui va bene chiamare getCurrentUser: siamo post-login, con token già salvato
        const userData = await authService.getCurrentUser(token);
        localStorage.setItem('user', JSON.stringify(userData));
        if (userData?.preferredLanguage && ['it', 'en', 'de', 'es', 'fr'].includes(userData.preferredLanguage)) {
          localStorage.setItem('app_language', userData.preferredLanguage);
          window.dispatchEvent(new Event('app-language-updated'));
        }
        setUser(normalizeUser(userData));
      } catch (err) {
        console.error('Failed to fetch user:', err);
        await logout(true); // sessione non valida / errore recupero utente
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heartbeat: aggiorna lastAccess ogni ~2.5 minuti
  useEffect(() => {
    const token = localStorage.getItem('token');

    // reset qualsiasi timer precedente
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }

    if (user && token) {
      const doPing = () => authService.ping(token).catch(() => {});
      // ping subito per settare lastAccess
      doPing();
      // intervallo 150s (~2.5 min)
      pingTimerRef.current = setInterval(doPing, 150 * 1000);
    }

    return () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };
  }, [user]);

  const login = async (credentials) => {
    try {
      const { accessToken, user: loginUser } = await authService.login(credentials);

      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(loginUser));
      if (loginUser?.preferredLanguage && ['it', 'en', 'de', 'es', 'fr'].includes(loginUser.preferredLanguage)) {
        localStorage.setItem('app_language', loginUser.preferredLanguage);
        window.dispatchEvent(new Event('app-language-updated'));
      }

      setUser(normalizeUser(loginUser));
      setError(null);

      // NON chiamare getCurrentUser qui: è proprio ciò che può causare 500 per utenti “speciali”
      return loginUser;
    } catch (err) {
      console.error('Login failed with error:', err);
      setError('Invalid login credentials');
      throw err;
    }
  };

  const register = async (data) => {
    try {
      const response = await authService.register(data);
      setError(null);
      // Nessun login automatico: attendiamo verifica email
      return response;
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Registration failed');
      throw err;
    }
  };

  const updateUserDiscount = (newDiscount) => {
    setUser((prevUser) => (prevUser ? { ...prevUser, discount: newDiscount } : prevUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        loading,
        error,
        setUser,
        updateUserDiscount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, AuthContext, useAuth };
